import { useEffect, useState } from 'react';

/**
 * PWA install prompt.
 *
 * Chrome/Edge fire `beforeinstallprompt`, which we capture and re-offer on our
 * own terms. iOS Safari has no such event, so it gets the manual Share → Add to
 * Home Screen instructions instead — that is the only way to install there.
 */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'society-install-dismissed';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as { standalone?: boolean }).standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISSED_KEY)) return;

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS never fires the event, so offer the manual route there.
    if (isIos()) setShowIosHelp(true);

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const visible = Boolean(deferred) || showIosHelp;

  // The banner is fixed to the bottom, so the page needs matching padding or it
  // sits on top of the last rows of whatever is being read.
  useEffect(() => {
    document.body.classList.toggle('install-open', visible);
    return () => document.body.classList.remove('install-open');
  }, [visible]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDeferred(null);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div className="install">
      <div className="install-body">
        <strong>Add to your home screen</strong>
        {deferred ? (
          <p className="muted">
            Install the app for one-tap access to fixtures and live scores, and it keeps working
            when the court WiFi does not.
          </p>
        ) : (
          <p className="muted">
            In Safari, tap <strong>Share</strong> then <strong>Add to Home Screen</strong>.
          </p>
        )}
      </div>
      <div className="install-actions">
        {deferred && (
          <button type="button" className="btn primary" onClick={install}>
            Install
          </button>
        )}
        <button type="button" className="btn ghost" onClick={dismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}
